import React, { createContext, useContext, useMemo } from 'react';
import type { RiskLevel, TraceStatus } from './types';

export interface ObsThemeTokens {
  isLight: boolean;
  page: string;
  card: string;
  cardHeaderBorder: string;
  title: string;
  subtitle: string;
  muted: string;
  text: string;
  textSecondary: string;
  border: string;
  borderSubtle: string;
  surface: string;
  surfaceMuted: string;
  surfaceHover: string;
  input: string;
  select: string;
  tableHead: string;
  tableHeadText: string;
  tableDivide: string;
  rowHover: string;
  rowSelected: string;
  fieldBorder: string;
  pre: string;
  skeleton: string;
  drawer: string;
  drawerHeader: string;
  btnSecondary: string;
  tabBarBorder: string;
  tabActive: string;
  tabInactive: string;
  link: string;
  accentText: string;
  chartGrid: string;
  chartTick: string;
  chartTooltipStyle: React.CSSProperties;
  statusBadge: Record<TraceStatus, string>;
  riskBadge: Record<RiskLevel, string>;
  providerBadge: Record<string, string>;
  providerDefault: string;
  warningBadge: string;
  statBox: string;
  messageCard: string;
  messageCardHeader: string;
  nodeCard: string;
  nodeHeader: string;
  alertCard: string;
  threatSafe: string;
  threatDanger: string;
  iconMuted: string;
  iconHover: string;
}

export function buildObsTheme(isLight: boolean): ObsThemeTokens {
  return {
    isLight,
    page: isLight ? 'text-slate-900' : 'text-zinc-100',
    card: isLight
      ? 'border-slate-200 bg-white shadow-sm'
      : 'border-white/[0.08] bg-white/[0.03] shadow-sm',
    cardHeaderBorder: isLight ? 'border-slate-100' : 'border-white/[0.06]',
    title: isLight ? 'text-slate-900' : 'text-zinc-100',
    subtitle: isLight ? 'text-slate-500' : 'text-zinc-500',
    muted: isLight ? 'text-slate-500' : 'text-zinc-500',
    text: isLight ? 'text-slate-800' : 'text-zinc-200',
    textSecondary: isLight ? 'text-slate-600' : 'text-zinc-400',
    border: isLight ? 'border-slate-200' : 'border-white/[0.08]',
    borderSubtle: isLight ? 'border-slate-100' : 'border-white/[0.06]',
    surface: isLight ? 'bg-slate-50' : 'bg-white/[0.03]',
    surfaceMuted: isLight ? 'bg-slate-50/50' : 'bg-white/[0.02]',
    surfaceHover: isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.04]',
    input: isLight
      ? 'border-slate-200 bg-white text-slate-700 placeholder:text-slate-400'
      : 'border-white/[0.1] bg-white/[0.04] text-zinc-200 placeholder:text-zinc-600',
    select: isLight
      ? 'border-slate-200 bg-white text-slate-600'
      : 'border-white/[0.1] bg-white/[0.04] text-zinc-300',
    tableHead: isLight ? 'bg-slate-50' : 'bg-white/[0.04]',
    tableHeadText: isLight ? 'text-slate-500' : 'text-zinc-500',
    tableDivide: isLight ? 'divide-slate-100' : 'divide-white/[0.06]',
    rowHover: isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.04]',
    rowSelected: isLight ? 'bg-indigo-50/50' : 'bg-indigo-500/10',
    fieldBorder: isLight ? 'border-slate-50' : 'border-white/[0.04]',
    pre: isLight
      ? 'bg-slate-50 border-slate-200 text-slate-700'
      : 'bg-black/30 border-white/[0.08] text-zinc-300',
    skeleton: isLight ? 'bg-slate-100' : 'bg-white/[0.08]',
    drawer: isLight
      ? 'bg-white border-slate-200'
      : 'bg-neutral-900 border-white/[0.1]',
    drawerHeader: isLight ? 'border-slate-100 bg-slate-50' : 'border-white/[0.06] bg-white/[0.03]',
    btnSecondary: isLight
      ? 'border-slate-200 bg-white text-slate-700'
      : 'border-white/[0.1] bg-white/[0.04] text-zinc-300',
    tabBarBorder: isLight ? 'border-slate-100' : 'border-white/[0.06]',
    tabActive: isLight ? 'border-indigo-600 text-indigo-700' : 'border-indigo-400 text-indigo-300',
    tabInactive: isLight
      ? 'border-transparent text-slate-500 hover:text-slate-700'
      : 'border-transparent text-zinc-500 hover:text-zinc-300',
    link: isLight ? 'text-indigo-600' : 'text-indigo-400',
    accentText: isLight ? 'text-indigo-700' : 'text-indigo-400',
    chartGrid: isLight ? '#e2e8f0' : '#3f3f46',
    chartTick: isLight ? '#64748b' : '#a1a1aa',
    chartTooltipStyle: isLight
      ? { fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a' }
      : { fontSize: 11, borderRadius: 8, border: '1px solid #3f3f46', background: '#18181b', color: '#fafafa' },
    statusBadge: {
      Success: isLight
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      Running: isLight
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      Failed: isLight
        ? 'bg-red-50 text-red-700 border-red-200'
        : 'bg-red-500/10 text-red-400 border-red-500/20',
      Blocked: isLight
        ? 'bg-violet-50 text-violet-700 border-violet-200'
        : 'bg-violet-500/10 text-violet-400 border-violet-500/20',
      'Waiting Approval': isLight
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      Retried: isLight
        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
        : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    },
    riskBadge: {
      Safe: isLight
        ? 'bg-slate-50 text-slate-600 border-slate-200'
        : 'bg-slate-500/10 text-slate-400 border-slate-500/20',
      Low: isLight
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      Medium: isLight
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      High: isLight
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      Critical: isLight
        ? 'bg-red-50 text-red-700 border-red-200'
        : 'bg-red-500/10 text-red-400 border-red-500/20',
    },
    providerBadge: {
      OpenAI: isLight
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      Anthropic: isLight
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      Mistral: isLight
        ? 'bg-violet-50 text-violet-700 border-violet-200'
        : 'bg-violet-500/10 text-violet-400 border-violet-500/20',
    },
    providerDefault: isLight
      ? 'bg-slate-50 text-slate-600 border-slate-200'
      : 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    warningBadge: isLight
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    statBox: isLight
      ? 'bg-slate-50 border-slate-100'
      : 'bg-white/[0.03] border-white/[0.06]',
    messageCard: isLight ? 'border-slate-200' : 'border-white/[0.08]',
    messageCardHeader: isLight ? 'bg-slate-50 border-slate-100' : 'bg-white/[0.03] border-white/[0.06]',
    nodeCard: isLight ? 'border-slate-200' : 'border-white/[0.08]',
    nodeHeader: isLight ? 'bg-slate-50 hover:bg-slate-100' : 'bg-white/[0.03] hover:bg-white/[0.06]',
    alertCard: isLight ? 'border-slate-100 bg-slate-50/50' : 'border-white/[0.06] bg-white/[0.02]',
    threatSafe: isLight
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    threatDanger: isLight
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-red-500/10 text-red-400 border-red-500/20',
    iconMuted: isLight ? 'text-slate-400' : 'text-zinc-500',
    iconHover: isLight ? 'hover:text-slate-600' : 'hover:text-zinc-300',
  };
}

const ObsThemeContext = createContext<ObsThemeTokens | null>(null);

export function ObsThemeProvider({ isLight, children }: { isLight: boolean; children: React.ReactNode }) {
  const theme = useMemo(() => buildObsTheme(isLight), [isLight]);
  return <ObsThemeContext.Provider value={theme}>{children}</ObsThemeContext.Provider>;
}

export function useObsTheme(): ObsThemeTokens {
  const ctx = useContext(ObsThemeContext);
  if (!ctx) throw new Error('useObsTheme must be used within ObsThemeProvider');
  return ctx;
}
